import { endOfYear, startOfYear } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  useCallback,
  type FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DateRange } from "react-day-picker";

import { FIVE, FOUR, HUNDRED, ONE, TWO, ZERO } from "@/app/constants/constants";
import { useQueryParams } from "@/hooks/query/useQueryParams";
import { cn, generateYearRange } from "@/utils/core/generalUtils";

export interface YearSliderFilterProps {
  filterValPrefix?: string;
  className?: string;
  selectedDate?: DateRange;
  onYearChange?: (dateRange: DateRange) => void;
  syncWithQuery?: boolean;
  backYearOffset?: number;
  forwardYearOffset?: number;
}

// Constants
export const YEAR_SLIDER_FILTER_KEY = "dateRange";
const YEAR_ITEM_WIDTH = 55;
const ARROW_BUTTONS_WIDTH = 70;
const SHOW_COUNT = 3;

const getYearSliderKey = (prefix: string): string => {
  return `${prefix}${YEAR_SLIDER_FILTER_KEY}`;
};

const calculateStartIndex = (
  selectedYear: number | null,
  years: number[],
): number => {
  const currentYear = new Date().getFullYear();
  const targetYear = selectedYear || currentYear;

  const yearIndex = years.indexOf(targetYear);
  if (yearIndex === -1) {
    return ZERO;
  }

  const newIndex = yearIndex - Math.floor(SHOW_COUNT / TWO);
  const maxStartIndex = years.length - SHOW_COUNT;

  return Math.max(ZERO, Math.min(newIndex, maxStartIndex));
};

const YearSliderFilter: FC<YearSliderFilterProps> = ({
  filterValPrefix,
  className,
  onYearChange,
  selectedDate,
  syncWithQuery = true,
  backYearOffset = FIVE,
  forwardYearOffset = FOUR,
}) => {
  const { clearParam, getAllValue, replaceSameKeys } = useQueryParams();
  const lastCenteredYear = useRef<number | null>(null);

  const itemWidthPercent = HUNDRED / SHOW_COUNT;
  const containerWidth = SHOW_COUNT * YEAR_ITEM_WIDTH + ARROW_BUTTONS_WIDTH;

  const YEARS = useMemo(
    () => generateYearRange(backYearOffset, forwardYearOffset),
    [backYearOffset, forwardYearOffset],
  );

  // Determine the correct filter key based on prefix
  const filterKey = useMemo(
    () =>
      filterValPrefix
        ? getYearSliderKey(filterValPrefix)
        : YEAR_SLIDER_FILTER_KEY,
    [filterValPrefix],
  );

  const queryValues = syncWithQuery ? getAllValue(filterKey) : undefined;

  const selectedYear = useMemo(() => {
    const [start, end] =
      !syncWithQuery && selectedDate
        ? [selectedDate.from, selectedDate.to]
        : (queryValues?.map((d) => (d ? new Date(d) : null)) ?? []);
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      return null;
    }

    const isFullYear =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === 0 &&
      end.getMonth() === 11;

    return isFullYear ? start.getFullYear() : null;
  }, [queryValues, selectedDate, syncWithQuery]);

  const [startIndex, setStartIndex] = useState(
    calculateStartIndex(selectedYear, YEARS),
  );

  const handleNext = useCallback(() => {
    setStartIndex((prev) =>
      prev + SHOW_COUNT < YEARS.length ? prev + ONE : prev,
    );
  }, [YEARS]);

  const handlePrev = useCallback(() => {
    setStartIndex((prev) => (prev > ZERO ? prev - ONE : prev));
  }, []);

  const handleSelectYear = useCallback(
    (year: number) => {
      // Handle deselection
      if (selectedYear === year) {
        onYearChange?.({ from: undefined, to: undefined });
        lastCenteredYear.current = null;

        if (syncWithQuery) {
          clearParam(filterKey);
        }

        return;
      }

      // Handle new selection
      const startDate = startOfYear(new Date(year, 0, 1));
      const endDate = endOfYear(new Date(year, 11, 31));

      onYearChange?.({ from: startDate, to: endDate });

      if (syncWithQuery) {
        replaceSameKeys(filterKey, [
          startDate.toDateString(),
          endDate.toDateString(),
        ]);
      }
    },
    [
      selectedYear,
      syncWithQuery,
      filterKey,
      onYearChange,
      clearParam,
      replaceSameKeys,
    ],
  );

  /**
   * AUTO FOCUS LOGIC
   * Whenever selectedYear changes, adjust startIndex to make the year visible
   * defaults to currentYear
   */
  useEffect(() => {
    if (!selectedYear || selectedYear === lastCenteredYear.current) {
      return;
    }

    setStartIndex(calculateStartIndex(selectedYear, YEARS));
    lastCenteredYear.current = selectedYear;
  }, [selectedYear, YEARS]);

  return (
    <div
      role='group'
      aria-label='Year Filter'
      className={cn("border rounded-full flex items-center px-1", className)}
      style={{ width: `${containerWidth}px` }}
    >
      {/* Arrow Left */}
      <button
        onClick={handlePrev}
        aria-label='Previous years'
        disabled={startIndex === ZERO}
        className='p-1.5 rounded-full transition-colors hover:bg-muted cursor-pointer disabled:text-gray-300'
      >
        <ChevronLeft size={16} />
      </button>

      {/* Years Container */}
      <div className='relative flex-1 overflow-hidden w-full'>
        <div
          className='py-1 flex transition-transform duration-300 ease-in-out items-center'
          role='listbox'
          aria-label='Select a year'
          style={{
            transform: `translateX(-${startIndex * itemWidthPercent}%)`,
          }}
        >
          {YEARS.map((year) => (
            <div
              key={year}
              style={{ minWidth: `${itemWidthPercent}%` }}
              className='flex justify-center items-center'
            >
              <button
                type='button'
                onClick={() => handleSelectYear(year)}
                className={cn(
                  "px-3 py-1 rounded-full cursor-pointer transition-all text-xs font-medium whitespace-nowrap",
                  selectedYear === year
                    ? "bg-primary text-white"
                    : "hover:bg-primary/10 hover:text-primary",
                )}
              >
                {year}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Arrow Right */}
      <button
        onClick={handleNext}
        aria-label='Next years'
        disabled={startIndex + SHOW_COUNT >= YEARS.length}
        className='p-1.5 rounded-full transition-colors hover:bg-muted cursor-pointer disabled:text-gray-300'
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

YearSliderFilter.displayName = "YearSliderFilter";

export default YearSliderFilter;
